import type { BlogPost } from './blog-types';
import { blogAPI } from './blog-api';
import { showError } from './modal';

/**
 * Blog UI Controller
 * Handles rendering and interactions for the blog section
 */
export class BlogUI {
  private static instance: BlogUI;
  private currentPage: number = 1;
  private isLoading: boolean = false;
  private autoRefreshInterval: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): BlogUI {
    if (!BlogUI.instance) {
      BlogUI.instance = new BlogUI();
    }
    return BlogUI.instance;
  }

  /**
   * Initialize the blog UI (for homepage preview)
   */
  async initialize(): Promise<void> {
    await this.loadBlogPreview();
    this.startAutoRefresh();
  }

  /**
   * Load blog preview for homepage (latest 3 posts)
   */
  async loadBlogPreview(): Promise<void> {
    const container = document.getElementById('blogPreviewContainer');
    if (!container) return; // Not on homepage

    try {
      const response = await blogAPI.getBlogPosts(1, 3);

      if (response.success && response.posts) {
        this.renderBlogPreview(response.posts, container);
      }
    } catch (error) {
      console.error('Error loading blog preview:', error);
    }
  }

  /**
   * Render blog preview posts
   */
  private renderBlogPreview(posts: BlogPost[], container: HTMLElement): void {
    if (posts.length === 0) {
      container.innerHTML = `
        <div class="no-posts">
          <p>👽 No alien transmissions yet. Check back soon!</p>
        </div>
      `;
      return;
    }

    container.innerHTML = posts.map(post => this.createPreviewCard(post)).join('');
  }

  /**
   * Create preview card for homepage
   */
  private createPreviewCard(post: BlogPost): string {
    const publishedDate = new Date(post.publishedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return `
      <article class="blog-card" onclick="window.location.href='blog-post.html?id=${post.id}'">
        <div class="blog-card-image">
          <img src="${post.imageUrl}" alt="${post.title}" loading="lazy">
          <div class="blog-card-tags">
            ${post.tags.slice(0, 2).map(tag => `<span class="blog-tag">${tag}</span>`).join('')}
          </div>
        </div>
        <div class="blog-card-content">
          <div class="blog-meta">
            <span class="blog-date">${publishedDate}</span>
            <span class="blog-read-time">⏱️ ${post.readTime} min</span>
          </div>
          <h3 class="blog-title">${post.title}</h3>
          <p class="blog-excerpt">${post.excerpt}</p>
          <div class="blog-card-actions">
            <button class="blog-read-more">
              Read More →
            </button>
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Load and display blog posts
   */
  async loadBlogPosts(page: number = 1): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const response = await blogAPI.getBlogPosts(page, 6);

      if (response.success && response.posts) {
        this.currentPage = page;
        this.renderBlogPosts(response.posts);
      } else {
        this.showError(response.message || 'Failed to load blog posts');
      }
    } catch (error) {
      console.error('Error loading blog posts:', error);
      this.showError('Cosmic interference detected!');
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  /**
   * Render blog posts to the DOM
   */
  private renderBlogPosts(posts: BlogPost[]): void {
    const container = document.getElementById('blogPostsContainer');
    if (!container) return;

    if (posts.length === 0) {
      container.innerHTML = `
        <div class="no-posts">
          <p>👽 No alien transmissions detected yet...</p>
          <button class="btn-primary" onclick="window.blogUI.generateNewPost()">
            Generate First Post
          </button>
        </div>
      `;
      return;
    }

    container.innerHTML = posts.map(post => this.createPostCard(post)).join('');
  }

  /**
   * Create HTML for a single blog post card
   */
  private createPostCard(post: BlogPost): string {
    const publishedDate = new Date(post.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Build author HTML - make clickable if authorWallet exists
    const authorHTML = post.authorWallet
      ? `<a href="profile.html?address=${post.authorWallet}" class="blog-author blog-author-clickable" onclick="event.stopPropagation()">👽 ${post.author}</a>`
      : `<span class="blog-author">👽 ${post.author}</span>`;

    return `
      <article class="blog-card" data-post-id="${post.id}">
        <div class="blog-card-image">
          <img src="${post.imageUrl}" alt="${post.title}" loading="lazy">
          <div class="blog-card-tags">
            ${post.tags.slice(0, 2).map(tag => `<span class="blog-tag">${tag}</span>`).join('')}
          </div>
        </div>
        <div class="blog-card-content">
          <div class="blog-meta">
            ${authorHTML}
            <span class="blog-date">${publishedDate}</span>
            <span class="blog-read-time">⏱️ ${post.readTime} min read</span>
          </div>
          <h3 class="blog-title">${post.title}</h3>
          <p class="blog-excerpt">${post.excerpt}</p>
          <div class="blog-card-actions">
            <button class="blog-read-more" onclick="window.blogUI.openPost('${post.id}')">
              Read More →
            </button>
            ${post.source ? `<a href="${post.source}" target="_blank" class="blog-source">View Source</a>` : ''}
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Open full blog post in modal
   */
  async openPost(postId: string): Promise<void> {
    const post = await blogAPI.getBlogPost(postId);
    if (!post) {
      showError('Post not found in the cosmic database!');
      return;
    }

    const modal = document.getElementById('blogModal');
    const modalContent = document.getElementById('blogModalContent');

    if (!modal || !modalContent) return;

    const publishedDate = new Date(post.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    modalContent.innerHTML = `
      <div class="blog-post-full">
        <img src="${post.imageUrl}" alt="${post.title}" class="blog-post-image">
        <div class="blog-post-header">
          <h1>${post.title}</h1>
          <div class="blog-meta">
            <span>👽 ${post.author}</span>
            <span>📅 ${publishedDate}</span>
            <span>⏱️ ${post.readTime} min read</span>
          </div>
          <div class="blog-tags">
            ${post.tags.map(tag => `<span class="blog-tag">${tag}</span>`).join('')}
          </div>
        </div>
        <div class="blog-post-content">
          ${this.formatContent(post.content)}
        </div>
        ${post.source ? `
          <div class="blog-post-source">
            <strong>Source:</strong> <a href="${post.source}" target="_blank">${post.source}</a>
          </div>
        ` : ''}
      </div>
    `;

    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close blog modal
   */
  closeModal(): void {
    const modal = document.getElementById('blogModal');
    if (modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  /**
   * Format blog content (convert markdown-like syntax to HTML)
   */
  private formatContent(content: string): string {
    return content
      .replace(/\n\n/g, '</p><p>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^/g, '<p>')
      .replace(/$/g, '</p>');
  }

  /**
   * Generate a new blog post
   */
  async generateNewPost(): Promise<void> {
    const generateBtn = document.getElementById('generateBlogBtn') as HTMLButtonElement;
    if (generateBtn) {
      generateBtn.disabled = true;
      generateBtn.textContent = '🛸 Generating...';
    }

    this.showLoading('Contacting the mothership for latest alien intel...');

    try {
      const response = await blogAPI.autoGenerateBlogPost();

      if (response.success && response.post) {
        // Reload blog posts to show the new one
        await this.loadBlogPosts(1);
        this.showSuccess('New alien transmission received! 👽');
      } else {
        this.showError(response.message || response.error || 'Generation failed');
      }
    } catch (error) {
      console.error('Error generating post:', error);
      this.showError('Failed to generate post. Try again!');
    } finally {
      if (generateBtn) {
        generateBtn.disabled = false;
        generateBtn.textContent = '🚀 Generate New Post';
      }
      this.hideLoading();
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Close modal on background click
    const modal = document.getElementById('blogModal');
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeModal();
        }
      });
    }

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  /**
   * Start auto-refresh for new posts
   */
  private startAutoRefresh(intervalMinutes: number = 30): void {
    // Clear existing interval
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }

    // Set new interval
    this.autoRefreshInterval = setInterval(() => {
      console.log('Auto-refreshing blog posts...');
      this.loadBlogPosts(this.currentPage);
    }, intervalMinutes * 60 * 1000);
  }

  /**
   * Stop auto-refresh
   */
  stopAutoRefresh(): void {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
      this.autoRefreshInterval = null;
    }
  }

  /**
   * Show loading indicator
   */
  private showLoading(message: string = 'Loading alien transmissions...'): void {
    const container = document.getElementById('blogPostsContainer');
    if (container) {
      container.innerHTML = `
        <div class="blog-loading">
          <div class="loading-spinner"></div>
          <p>${message}</p>
        </div>
      `;
    }
  }

  /**
   * Hide loading indicator
   */
  private hideLoading(): void {
    // Loading will be replaced by content
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const container = document.getElementById('blogPostsContainer');
    if (container) {
      container.innerHTML = `
        <div class="blog-error">
          <p>🛸 ${message}</p>
          <button class="btn-primary" onclick="window.blogUI.loadBlogPosts()">
            Try Again
          </button>
        </div>
      `;
    }
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'blog-notification success';
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 100);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Load next page
   */
  nextPage(): void {
    this.loadBlogPosts(this.currentPage + 1);
  }

  /**
   * Load previous page
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.loadBlogPosts(this.currentPage - 1);
    }
  }
}

// Export singleton instance
export const blogUI = BlogUI.getInstance();
